import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Upload, 
  FileSpreadsheet, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Download,
  Loader2,
  X,
  Info,
  Users,
  UserCheck,
  Globe,
  MapPin,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_FILE_SIZE_MB = 10;
const MAX_ROWS = 20000;

const LEAD_FIELDS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'lead_source_detail', label: 'Source' },
  { value: 'product_interest', label: 'Product Interest' },
  { value: 'notes', label: 'Notes' },
  { value: 'qualification_notes', label: 'Qualification Notes' },
  { value: 'state', label: 'State' },
  { value: 'region', label: 'Region' },
  { value: 'preferred_contact_method', label: 'Preferred Contact Method' },
  { value: 'language_preference', label: 'Language Preference' },
];

const STEPS = [
  { id: 'upload', label: 'Upload CSV', number: 1 },
  { id: 'mapping', label: 'Map Columns', number: 2 },
  { id: 'preview', label: 'Preview & Validate', number: 3 },
  { id: 'execute', label: 'Import', number: 4 },
];

export default function ImportLeadsPage() {
  const navigate = useNavigate();
  const { hasPermission, role } = usePermissions();
  const canImport = hasPermission('leads_import');
  const canSelectStage = ['owner', 'admin'].includes(role); // Only Owner/Admin can select target stage

  // Data source state
  const [dataSource, setDataSource] = useState('csv');
  const [dataSources, setDataSources] = useState([]);

  // Source leads state (for landing_page / field_data)
  const [sourceLeads, setSourceLeads] = useState([]);
  const [sourceLeadsLoading, setSourceLeadsLoading] = useState(false);
  const [sourceManualOwners, setSourceManualOwners] = useState({});
  const [sourceAssignmentMode, setSourceAssignmentMode] = useState('round_robin');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  // One-click staging migration (Owner/Admin) — moves unassigned landing-page / field-data
  // leads stuck in cold_data back to their hidden staging stages.
  const [isMigrating, setIsMigrating] = useState(false);

  // Step state
  const [currentStep, setCurrentStep] = useState('upload');
  
  // File state
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Mapping state
  const [columnMapping, setColumnMapping] = useState({});
  
  // Options state
  const [duplicateBehavior, setDuplicateBehavior] = useState('skip');
  const [targetStage, setTargetStage] = useState('cold_data'); // Target stage for imported leads
  
  // Owner assignment state
  const [ownerAssignmentMode, setOwnerAssignmentMode] = useState('round_robin');
  const [allUsers, setAllUsers] = useState([]); // All assignable users (AE + SM)
  const [accountExecutives, setAccountExecutives] = useState([]); // Only AEs for round-robin
  const [manualOwners, setManualOwners] = useState({}); // row_num -> user_id
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [importBatchId, setImportBatchId] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  
  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Lead stages for dropdown
  const LEAD_STAGES = [
    { value: 'cold_data', label: 'Cold Data' },
    { value: 'attempting_contact', label: 'Attempting Contact' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'consultation_scheduled', label: 'Consultation Scheduled' },
    { value: 'consultation_completed', label: 'Consultation Completed' },
    { value: 'warm_follow_up', label: 'Warm Follow-Up' },
    { value: 'rsvp_office_event', label: 'RSVP Office Event' },
    { value: 'attended_office_event', label: 'Attended Office Event' },
    { value: 'long_term_nurture', label: 'Long-Term Nurture' }
  ];

  // Load users for assignment
  useEffect(() => {
    if (canImport) {
      fetchUsers();
      fetchDataSources();
    }
  }, [canImport]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDataSources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/import/sources`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDataSources(data.sources || []);
      }
    } catch (error) {
      logger.error('Failed to fetch data sources:', error);
    }
  };

  // Owner/Admin: move unassigned landing-page / field-data leads stuck in cold_data
  // back to their hidden staging stages. Surface the count in a friendly toast.
  const handleMigrateStagingStages = async () => {
    if (!canSelectStage) return;
    const ok = window.confirm(
      'This will move unassigned landing-page & field-data leads out of the "Cold Data" stage ' +
      'and back into the hidden triage queue so you can assign owners here.\n\nContinue?'
    );
    if (!ok) return;
    setIsMigrating(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/migrate-staging-stages`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Migration failed');
      } else {
        const d = await r.json();
        const total = d.total_moved || 0;
        if (total === 0) {
          toast.success('No stuck leads found — already clean ✨');
        } else {
          toast.success(
            `Moved ${total} lead${total === 1 ? '' : 's'} back to triage ` +
            `(${d.moved_landing_page} landing-page · ${d.moved_field_data} field-data)`
          );
          // Refresh counts so the data-source tabs show the new pending totals
          fetchDataSources();
        }
      }
    } catch (err) {
      logger.error('Migration error', err);
      toast.error('Migration failed — check network');
    }
    setIsMigrating(false);
  };

  const fetchSourceLeads = async (source) => {
    setSourceLeadsLoading(true);
    setSourceLeads([]);
    setSourceManualOwners({});
    setAssignResult(null);
    try {
      const response = await fetch(`${API_URL}/api/leads/import/source-leads?source=${source}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSourceLeads(data.leads || []);
      }
    } catch (error) {
      logger.error('Failed to fetch source leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setSourceLeadsLoading(false);
    }
  };

  const handleSourceManualOwnerChange = (leadId, userId) => {
    setSourceManualOwners(prev => ({ ...prev, [leadId]: userId }));
  };

  const handleAssignSourceLeads = async () => {
    const assignments = {};
    
    if (sourceAssignmentMode === 'round_robin') {
      // Auto-assign using round-robin across AEs — skip already assigned and duplicates
      if (accountExecutives.length === 0) {
        toast.error('No active Account Executives available for round-robin assignment.');
        return;
      }
      const assignableLeads = sourceLeads.filter(lead => 
        (!lead.assigned_to || lead.assigned_to === '') && !lead.is_duplicate
      );
      if (assignableLeads.length === 0) {
        toast.error('No unassigned, non-duplicate leads available for assignment.');
        return;
      }
      assignableLeads.forEach((lead, idx) => {
        assignments[lead.lead_id] = accountExecutives[idx % accountExecutives.length].user_id;
      });
    } else {
      // Manual assignments — skip already assigned leads
      for (const lead of sourceLeads) {
        if (lead.assigned_to && lead.assigned_to !== '') continue;
        if (sourceManualOwners[lead.lead_id]) {
          assignments[lead.lead_id] = sourceManualOwners[lead.lead_id];
        }
      }
      if (Object.keys(assignments).length === 0) {
        toast.error('Please assign at least one owner');
        return;
      }
    }

    setIsAssigning(true);
    try {
      const response = await fetch(`${API_URL}/api/leads/import/assign-source-leads`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments, source: dataSource })
      });
      if (response.ok) {
        const data = await response.json();
        setAssignResult(data);
        toast.success(`${data.assigned_count} leads assigned successfully`);
        fetchDataSources(); // Refresh counts
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Assignment failed');
      }
    } catch (error) {
      logger.error('Assignment error:', error);
      toast.error('Failed to assign leads');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleDownloadSourceLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/import/download-source-leads?source=${dataSource}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${dataSource}_leads.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Download started');
      }
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const handleDataSourceChange = (source) => {
    setDataSource(source);
    setAssignResult(null);
    if (source !== 'csv') {
      fetchSourceLeads(source);
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch all assignable users (AE + SM)
      const allResponse = await fetch(`${API_URL}/api/leads/import/users?for_auto_assign=false`, {
        credentials: 'include'
      });
      if (allResponse.ok) {
        const data = await allResponse.json();
        setAllUsers(data);
      }
      
      // Fetch only AEs for round-robin
      const aeResponse = await fetch(`${API_URL}/api/leads/import/users?for_auto_assign=true`, {
        credentials: 'include'
      });
      if (aeResponse.ok) {
        const data = await aeResponse.json();
        setAccountExecutives(data);
      }
    } catch (error) {
      logger.error('Failed to fetch users:', error);
    }
  };

  // File handling
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = useCallback((selectedFile) => {
    setFileError(null);
    setFile(null);
    setCsvHeaders([]);
    setCsvPreview([]);
    setColumnMapping({});
    setManualOwners({});

    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      setFileError('Please upload a CSV file');
      return;
    }

    // Validate file size
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      setFileError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }

    // Read and parse CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          setFileError('CSV file is empty');
          return;
        }

        if (lines.length > MAX_ROWS + 1) {
          setFileError(`File exceeds ${MAX_ROWS.toLocaleString()} rows limit`);
          return;
        }

        // Parse headers
        const headers = parseCSVLine(lines[0]);
        setCsvHeaders(headers);

        // Auto-map columns where names match
        const autoMapping = {};
        headers.forEach(header => {
          const normalizedHeader = header.toLowerCase().replace(/[_\s-]/g, '');
          LEAD_FIELDS.forEach(field => {
            const normalizedField = field.value.toLowerCase().replace(/[_\s-]/g, '');
            if (normalizedHeader === normalizedField || 
                normalizedHeader.includes(normalizedField) ||
                normalizedField.includes(normalizedHeader)) {
              autoMapping[header] = field.value;
            }
          });
        });
        setColumnMapping(autoMapping);

        // Parse preview rows (first 5)
        const previewRows = [];
        for (let i = 1; i < Math.min(6, lines.length); i++) {
          const values = parseCSVLine(lines[i]);
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx] || '';
          });
          previewRows.push(row);
        }
        setCsvPreview(previewRows);

        setFile(selectedFile);
        toast.success(`File loaded: ${lines.length - 1} rows detected`);
      } catch (error) {
        setFileError('Failed to parse CSV file');
        logger.error(error);
      }
    };
    reader.readAsText(selectedFile);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    processFile(droppedFile);
  }, [processFile]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  }, [processFile]);

  // Simple CSV line parser (handles quoted fields)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleMappingChange = (csvColumn, leadField) => {
    setColumnMapping(prev => {
      const newMapping = { ...prev };
      if (leadField === 'none') {
        delete newMapping[csvColumn];
      } else {
        newMapping[csvColumn] = leadField;
      }
      return newMapping;
    });
  };

  const getMappedFieldsForColumn = (excludeColumn) => {
    const mappedValues = Object.entries(columnMapping)
      .filter(([col]) => col !== excludeColumn)
      .map(([, field]) => field);
    return mappedValues;
  };

  // Update manual owner for a specific row
  const handleManualOwnerChange = (rowNum, userId) => {
    setManualOwners(prev => ({
      ...prev,
      [rowNum]: userId
    }));
  };

  // Calculate actual importable count based on current state
  const getImportableCount = () => {
    if (!previewData?.preview_rows) return 0;
    
    let count = 0;
    for (const row of previewData.preview_rows) {
      // Check for duplicates - handle both boolean and string values
      const isDuplicate = row.is_intra_duplicate === true || row.is_intra_duplicate === 'true' ||
                         row.is_db_duplicate === true || row.is_db_duplicate === 'true';
      const isSkippedDuplicate = isDuplicate && duplicateBehavior === 'skip';
      
      if (isSkippedDuplicate) continue;
      if (row.status === 'invalid') continue;
      
      // For manual mode, check if owner is assigned (either from preview or manually)
      if (ownerAssignmentMode === 'manual') {
        const hasOwner = manualOwners[row.row_num] || row.assigned_owner_id;
        if (hasOwner) count++;
      } else {
        // Round-robin mode - all valid non-duplicate rows will be imported
        count++;
      }
    }
    return count;
  };

  // Count rows that can be imported but need owner assignment in manual mode
  const getRowsNeedingOwner = () => {
    if (!previewData?.preview_rows || ownerAssignmentMode !== 'manual') return 0;
    
    let count = 0;
    for (const row of previewData.preview_rows) {
      // Check for duplicates - handle both boolean and string values
      const isDuplicate = row.is_intra_duplicate === true || row.is_intra_duplicate === 'true' ||
                         row.is_db_duplicate === true || row.is_db_duplicate === 'true';
      const isSkippedDuplicate = isDuplicate && duplicateBehavior === 'skip';
      
      // Skip duplicates and invalid rows - they won't be imported
      if (isSkippedDuplicate) continue;
      if (row.status === 'invalid') continue;
      
      const hasOwner = manualOwners[row.row_num] || row.assigned_owner_id;
      if (!hasOwner) count++;
    }
    return count;
  };

  // Validation - call preview API
  const handleValidate = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    // Check for AEs if using round-robin
    if (ownerAssignmentMode === 'round_robin' && accountExecutives.length === 0) {
      toast.error('No active Account Executives available for round-robin assignment. Please use manual assignment or create AE accounts first.');
      return;
    }

    const mappedFields = Object.values(columnMapping);
    const hasName = mappedFields.includes('first_name') || mappedFields.includes('last_name');
    const hasContact = mappedFields.includes('phone') || mappedFields.includes('email');

    if (!hasName) {
      toast.error('Please map at least first_name or last_name');
      return;
    }

    if (!hasContact) {
      toast.error('Please map at least phone or email');
      return;
    }

    setIsValidating(true);
    setPreviewData(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(columnMapping));
      formData.append('duplicate_behavior', duplicateBehavior);
      formData.append('owner_assignment_mode', ownerAssignmentMode);
      formData.append('target_stage', targetStage); // Include target stage
      
      // For round-robin, send AE user IDs as pool
      if (ownerAssignmentMode === 'round_robin') {
        formData.append('owner_pool', JSON.stringify(accountExecutives.map(u => u.user_id)));
      } else {
        formData.append('owner_pool', '[]');
      }
      
      formData.append('manual_owners', JSON.stringify(manualOwners));

      const response = await fetch(`${API_URL}/api/leads/import/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setImportBatchId(data.import_batch_id);
        setCurrentStep('preview');
        toast.success('Validation complete');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Validation failed');
      }
    } catch (error) {
      logger.error('Validation error:', error);
      toast.error('Failed to validate file');
    } finally {
      setIsValidating(false);
    }
  };

  // Execute import
  const handleExecuteImport = async () => {
    if (!importBatchId) {
      toast.error('Missing import configuration');
      return;
    }

    // For manual mode, check that all valid non-duplicate rows have owners
    if (ownerAssignmentMode === 'manual' && previewData) {
      const rowsToImport = previewData.preview_rows.filter(r => {
        // Skip invalid rows
        if (r.status === 'invalid') return false;
        // Skip duplicates if set to skip
        const isDuplicate = r.is_intra_duplicate === true || r.is_intra_duplicate === 'true' ||
                           r.is_db_duplicate === true || r.is_db_duplicate === 'true';
        if (isDuplicate && duplicateBehavior === 'skip') return false;
        return true;
      });
      const missingOwners = rowsToImport.filter(r => !r.assigned_owner_id && !manualOwners[r.row_num]);
      if (missingOwners.length > 0) {
        toast.error(`${missingOwners.length} rows are missing owner assignment. Please assign owners to all rows.`);
        return;
      }
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const response = await fetch(`${API_URL}/api/leads/import/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          import_batch_id: importBatchId,
          mapping: columnMapping,
          duplicate_behavior: duplicateBehavior,
          owner_assignment_mode: ownerAssignmentMode,
          owner_pool: ownerAssignmentMode === 'round_robin' ? accountExecutives.map(u => u.user_id) : [],
          manual_owners: manualOwners,
          filename: file.name
        })
      });

      if (response.ok) {
        const data = await response.json();
        setImportResult(data);
        setCurrentStep('execute');
        toast.success(`Import complete: ${data.imported_count} leads imported`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      logger.error('Import error:', error);
      toast.error('Failed to execute import');
    } finally {
      setIsImporting(false);
    }
  };

  // Download error report
  const downloadErrorReport = () => {
    if (!importResult || !importResult.failed_rows || importResult.failed_rows.length === 0) {
      toast.info('No failed rows to download');
      return;
    }

    const headers = ['row_number', 'error_reason', ...Object.keys(importResult.failed_rows[0].data || {})];
    const csvRows = [headers.join(',')];

    importResult.failed_rows.forEach(row => {
      const values = [
        row.row_num,
        `"${(row.error_reason || '').replace(/"/g, '""')}"`,
        ...Object.values(row.data || {}).map(v => `"${String(v || '').replace(/"/g, '""')}"`)
      ];
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import_errors_${importBatchId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Error report downloaded');
  };

  const resetImport = () => {
    setFile(null);
    setCsvHeaders([]);
    setCsvPreview([]);
    setColumnMapping({});
    setPreviewData(null);
    setImportBatchId(null);
    setImportResult(null);
    setCurrentStep('upload');
    setDuplicateBehavior('skip');
    setOwnerAssignmentMode('round_robin');
    setManualOwners({});
  };

  // Access denied screen
  if (!canImport) {
    return (
      <div className="space-y-6" data-testid="import-leads-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Import Leads</h1>
        </div>
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You do not have permission to import leads. Only Admin and Sales Manager roles can access this feature.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  return (
    <div className="space-y-6" data-testid="import-leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Import & Triage Leads</h1>
          <p className="text-sm text-slate-500 mt-1">Upload a CSV, or assign owners to inbound Landing Page & Field Data submissions awaiting triage.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSelectStage && (
            <Button
              variant="outline"
              onClick={handleMigrateStagingStages}
              disabled={isMigrating}
              data-testid="migrate-staging-btn"
              title="Move old unassigned landing-page / field-data leads from Cold Data back into the triage queue"
            >
              {isMigrating ? 'Migrating…' : 'Recover stuck inbound leads'}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/leads')} data-testid="back-to-leads">
            Back to Leads
          </Button>
        </div>
      </div>

      {/* Data Source Selector */}
      <Card data-testid="data-source-selector">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Select Data Source</CardTitle>
          <p className="text-xs text-slate-500">Landing Page & Field Data submissions live in a hidden staging stage until an owner is assigned here — they never appear on the main pipeline until triaged.</p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            {(dataSources.length > 0 ? dataSources : [
              { id: 'csv', label: 'CSV Upload', unassigned_count: 0 },
              { id: 'landing_page', label: 'Landing Pages', unassigned_count: 0 },
              { id: 'field_data', label: 'Field Data', unassigned_count: 0 }
            ]).map((src) => (
              <button
                key={src.id}
                onClick={() => handleDataSourceChange(src.id)}
                data-testid={`source-${src.id}`}
                className={`flex-1 p-4 rounded-lg border-2 text-left transition-all
                  ${dataSource === src.id 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-slate-200 hover:border-slate-300 bg-white'}`}
              >
                <div className="flex items-center gap-2">
                  {src.id === 'csv' && <FileSpreadsheet className="w-5 h-5 text-slate-600" />}
                  {src.id === 'landing_page' && <Globe className="w-5 h-5 text-slate-600" />}
                  {src.id === 'field_data' && <MapPin className="w-5 h-5 text-slate-600" />}
                  <span className="font-medium text-slate-800">{src.label}</span>
                </div>
                {src.id !== 'csv' && src.unassigned_count > 0 && (
                  <p className="text-xs text-amber-600 font-medium mt-1">
                    {src.unassigned_count} unassigned lead{src.unassigned_count !== 1 ? 's' : ''}
                  </p>
                )}
                {src.id !== 'csv' && src.unassigned_count === 0 && (
                  <p className="text-xs text-slate-400 mt-1">No unassigned leads</p>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Source Leads View (Landing Pages / Field Data) */}
      {dataSource !== 'csv' && (
        <SourceLeadsView
          dataSource={dataSource}
          sourceLeads={sourceLeads}
          sourceLeadsLoading={sourceLeadsLoading}
          assignmentMode={sourceAssignmentMode}
          setAssignmentMode={setSourceAssignmentMode}
          manualOwners={sourceManualOwners}
          onManualOwnerChange={handleSourceManualOwnerChange}
          allUsers={allUsers}
          accountExecutives={accountExecutives}
          onAssign={handleAssignSourceLeads}
          onDownload={handleDownloadSourceLeads}
          isAssigning={isAssigning}
          assignResult={assignResult}
          onRefresh={() => { fetchSourceLeads(dataSource); fetchDataSources(); }}
        />
      )}

      {/* CSV Import Flow */}
      {dataSource === 'csv' && (
        <>
      {/* Progress Steps */}
      <div className="flex items-center justify-between max-w-2xl mx-auto mb-8">
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
              ${idx < currentStepIndex ? 'bg-green-500 text-white' : 
                idx === currentStepIndex ? 'bg-gold text-white' : 
                'bg-slate-200 text-slate-500'}`}
            >
              {idx < currentStepIndex ? <CheckCircle2 className="w-5 h-5" /> : step.number}
            </div>
            <span className={`ml-2 text-sm ${idx === currentStepIndex ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>
              {step.label}
            </span>
            {idx < STEPS.length - 1 && (
              <ArrowRight className="mx-4 text-slate-300 w-4 h-4" />
            )}
          </div>
        ))}
      </div>

      {/* Step: Upload */}
      {currentStep === 'upload' && (
        <Card data-testid="upload-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Upload CSV File
            </CardTitle>
            <CardDescription>
              Upload a CSV file with lead data. Maximum {MAX_FILE_SIZE_MB}MB or {MAX_ROWS.toLocaleString()} rows.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${isDragging ? 'border-gold bg-gold/5' : 'border-slate-200 hover:border-slate-300'}
                ${file ? 'bg-green-50 border-green-300' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              {file ? (
                <div className="space-y-2">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                  <p className="text-lg font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                  <Button variant="ghost" size="sm" onClick={resetImport}>
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-700 mb-1">
                    Drag and drop your CSV file here
                  </p>
                  <p className="text-sm text-slate-500 mb-4">or</p>
                  <label>
                    <Button variant="outline" asChild>
                      <span className="cursor-pointer">Browse Files</span>
                    </Button>
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileSelect}
                      data-testid="file-input"
                    />
                  </label>
                </>
              )}
            </div>

            {fileError && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{fileError}</AlertDescription>
              </Alert>
            )}

            {/* CSV Preview */}
            {csvPreview.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-slate-700">Preview (first 5 rows)</h3>
                <div className="border rounded-lg overflow-auto max-h-64">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {csvHeaders.map(header => (
                          <TableHead key={header} className="whitespace-nowrap">{header}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvPreview.map((row, idx) => (
                        <TableRow key={`preview-${idx}`}>
                          {csvHeaders.map(header => (
                            <TableCell key={header} className="whitespace-nowrap">
                              {row[header] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Next button */}
            {file && (
              <div className="flex justify-end">
                <Button onClick={() => setCurrentStep('mapping')} data-testid="next-to-mapping">
                  Next: Map Columns <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step: Mapping */}
      {currentStep === 'mapping' && (
        <Card data-testid="mapping-step">
          <CardHeader>
            <CardTitle>Map CSV Columns to Lead Fields</CardTitle>
            <CardDescription>
              Match your CSV columns to the corresponding lead fields. Required: at least one name field (first_name or last_name) and one contact field (phone or email).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Column Mapping */}
            <div className="grid gap-4">
              {csvHeaders.map(header => (
                <div key={header} className="flex items-center gap-4">
                  <div className="w-48 text-sm font-medium text-slate-700 truncate" title={header}>
                    {header}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <Select
                    value={columnMapping[header] || 'none'}
                    onValueChange={(value) => handleMappingChange(header, value)}
                  >
                    <SelectTrigger className="w-64" data-testid={`mapping-${header}`}>
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Skip this column --</SelectItem>
                      {LEAD_FIELDS.map(field => {
                        const isDisabled = getMappedFieldsForColumn(header).includes(field.value);
                        return (
                          <SelectItem 
                            key={field.value} 
                            value={field.value}
                            disabled={isDisabled}
                          >
                            {field.label} {isDisabled && '(already mapped)'}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {columnMapping[header] && (
                    <Badge variant="secondary">{columnMapping[header]}</Badge>
                  )}
                </div>
              ))}
            </div>

            {/* Import Options */}
            <div className="border-t pt-6 space-y-6">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Lead Owner Assignment
              </h3>
              
              {/* Owner Assignment Mode */}
              <div className="space-y-3">
                <Label>Assignment Mode</Label>
                <RadioGroup 
                  value={ownerAssignmentMode} 
                  onValueChange={setOwnerAssignmentMode}
                  className="space-y-3"
                >
                  <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                    <RadioGroupItem value="round_robin" id="round_robin" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="round_robin" className="font-medium cursor-pointer">
                        Round-Robin (Recommended)
                      </Label>
                      <p className="text-sm text-slate-500 mt-1">
                        Automatically distribute leads evenly across all active Account Executives.
                        {accountExecutives.length > 0 ? (
                          <span className="block mt-1 text-green-600">
                            {accountExecutives.length} Account Executive(s) available: {accountExecutives.map(u => u.name).join(', ')}
                          </span>
                        ) : (
                          <span className="block mt-1 text-red-600">
                            No active Account Executives found. Create AE accounts first or use manual assignment.
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50">
                    <RadioGroupItem value="manual" id="manual" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="manual" className="font-medium cursor-pointer">
                        Manual Assignment
                      </Label>
                      <p className="text-sm text-slate-500 mt-1">
                        Assign owners individually per row during the preview step. 
                        Can assign to Account Executives or Sales Managers.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Duplicate Behavior */}
              <div className="space-y-2 border-t pt-4">
                <Label>Duplicate Handling</Label>
                <RadioGroup 
                  value={duplicateBehavior} 
                  onValueChange={setDuplicateBehavior}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="skip" id="skip" />
                    <Label htmlFor="skip" className="font-normal cursor-pointer">
                      Skip duplicates (default)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="flag" id="flag" />
                    <Label htmlFor="flag" className="font-normal cursor-pointer">
                      Import and flag duplicates
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-slate-500 mt-1">
                  Duplicates are detected by phone number match within the file and existing database records.
                </p>
              </div>

              {/* Target Stage Selection - Owner/Admin only */}
              {canSelectStage && (
                <div className="space-y-2 border-t pt-4">
                  <Label>Target Lead Stage</Label>
                  <Select value={targetStage} onValueChange={setTargetStage}>
                    <SelectTrigger className="w-full md:w-64" data-testid="target-stage-select">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STAGES.map(stage => (
                        <SelectItem key={stage.value} value={stage.value}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Choose the stage for imported leads. Default is "Cold Data". Advanced stages are useful for migrating existing leads.
                  </p>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep('upload')}>
                <ArrowLeft className="mr-2 w-4 h-4" /> Back
              </Button>
              <Button 
                onClick={handleValidate} 
                disabled={isValidating || (ownerAssignmentMode === 'round_robin' && accountExecutives.length === 0)}
                data-testid="validate-btn"
              >
                {isValidating ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    Validate & Preview <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Preview */}
      {currentStep === 'preview' && previewData && (
        <Card data-testid="preview-step">
          <CardHeader>
            <CardTitle>Validation Results</CardTitle>
            <CardDescription>
              Review the validation results and owner assignments before importing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-slate-900">{previewData.total_rows}</div>
                  <div className="text-sm text-slate-500">Total Rows</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-600">{previewData.valid_count}</div>
                  <div className="text-sm text-slate-500">Valid</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-red-600">{previewData.invalid_count}</div>
                  <div className="text-sm text-slate-500">Invalid</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-amber-600">{previewData.warning_count}</div>
                  <div className="text-sm text-slate-500">Warnings</div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-blue-700 capitalize">
                    {ownerAssignmentMode.replace('_', ' ')}
                  </div>
                  <div className="text-sm text-blue-600">Assignment Mode</div>
                </CardContent>
              </Card>
            </div>

            {/* Duplicate Summary */}
            {(previewData.intra_duplicates_count > 0 || previewData.db_duplicates_count > 0) && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Duplicates Detected</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside text-sm">
                    {previewData.intra_duplicates_count > 0 && (
                      <li>{previewData.intra_duplicates_count} duplicate(s) within the uploaded file</li>
                    )}
                    {previewData.db_duplicates_count > 0 && (
                      <li>{previewData.db_duplicates_count} duplicate(s) matching existing database records</li>
                    )}
                  </ul>
                  <p className="mt-2 text-sm">
                    {duplicateBehavior === 'skip' 
                      ? 'These will be skipped during import.' 
                      : 'These will be imported and flagged as suspected duplicates.'}
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {/* Preview Table with Owner Column */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Preview with Owner Assignments (showing first {Math.min(100, previewData.preview_rows?.length || 0)} rows)
              </h3>
              <div className="border rounded-lg overflow-auto max-h-[500px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-white">
                    <TableRow>
                      <TableHead className="w-16">Row</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead>First Name</TableHead>
                      <TableHead>Last Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="w-48">
                        <span className="flex items-center gap-1">
                          <UserCheck className="w-4 h-4" />
                          Owner
                        </span>
                      </TableHead>
                      <TableHead>Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.preview_rows?.map((row) => {
                      // Check for duplicates - handle both boolean and string values
                      const isDuplicate = row.is_intra_duplicate === true || row.is_intra_duplicate === 'true' ||
                                         row.is_db_duplicate === true || row.is_db_duplicate === 'true';
                      const isSkippedDuplicate = isDuplicate && duplicateBehavior === 'skip';
                      const currentOwner = manualOwners[row.row_num] || row.assigned_owner_id;
                      const currentOwnerName = manualOwners[row.row_num] 
                        ? allUsers.find(u => u.user_id === manualOwners[row.row_num])?.name 
                        : row.assigned_owner_name;
                      
                      return (
                        <TableRow key={row.row_num} className={
                          row.status === 'invalid' ? 'bg-red-50' :
                          isSkippedDuplicate ? 'bg-slate-50 opacity-60' :
                          row.status === 'warning' ? 'bg-amber-50' : ''
                        }>
                          <TableCell>{row.row_num}</TableCell>
                          <TableCell>
                            {row.status === 'valid' && !isSkippedDuplicate && (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Valid
                              </Badge>
                            )}
                            {row.status === 'invalid' && (
                              <Badge className="bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3 mr-1" /> Invalid
                              </Badge>
                            )}
                            {row.status === 'warning' && !isSkippedDuplicate && (
                              <Badge className="bg-amber-100 text-amber-800">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Warning
                              </Badge>
                            )}
                            {isSkippedDuplicate && (
                              <Badge className="bg-slate-100 text-slate-600">
                                Skip (Dup)
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{row.data?.first_name || '-'}</TableCell>
                          <TableCell>{row.data?.last_name || '-'}</TableCell>
                          <TableCell>{row.data?.phone || '-'}</TableCell>
                          <TableCell>{row.data?.email || '-'}</TableCell>
                          <TableCell>
                            {isSkippedDuplicate ? (
                              <span className="text-slate-400">-</span>
                            ) : ownerAssignmentMode === 'manual' ? (
                              <Select
                                value={currentOwner || ''}
                                onValueChange={(value) => handleManualOwnerChange(row.row_num, value)}
                              >
                                <SelectTrigger className="w-40 h-8 text-xs" data-testid={`owner-select-${row.row_num}`}>
                                  <SelectValue placeholder="Select owner..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {allUsers.map(user => (
                                    <SelectItem key={user.user_id} value={user.user_id}>
                                      {user.name} ({user.role === 'account_executive' ? 'AE' : 'SM'})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : row.status === 'invalid' ? (
                              <span className="text-slate-400">-</span>
                            ) : (
                              <span className="text-sm font-medium text-blue-600">
                                {currentOwnerName || 'Auto-assigned'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            {row.errors?.length > 0 && (
                              <span className="text-red-600 text-xs">{row.errors.join('; ')}</span>
                            )}
                            {row.warnings?.length > 0 && (
                              <span className="text-amber-600 text-xs">{row.warnings.join('; ')}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Import Summary */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Import Summary</AlertTitle>
              <AlertDescription>
                <p>
                  <strong>{getImportableCount()}</strong> leads will be imported.
                  {previewData.invalid_count > 0 && 
                    ` ${previewData.invalid_count} invalid rows will be skipped.`}
                  {duplicateBehavior === 'skip' && (previewData.intra_duplicates_count + previewData.db_duplicates_count) > 0 && 
                    ` ${previewData.intra_duplicates_count + previewData.db_duplicates_count} duplicates will be skipped.`}
                  {duplicateBehavior === 'flag' && (previewData.intra_duplicates_count + previewData.db_duplicates_count) > 0 && 
                    ` ${previewData.intra_duplicates_count + previewData.db_duplicates_count} will be flagged as suspected duplicates.`}
                </p>
                {ownerAssignmentMode === 'manual' && (
                  <p className="mt-2 text-amber-600">
                    <strong>Note:</strong> Only rows with an assigned owner will be imported. 
                    {getRowsNeedingOwner() > 0 
                      ? ` ${getRowsNeedingOwner()} row(s) still need owner assignment.` 
                      : ` ${Object.keys(manualOwners).length} owner(s) assigned.`}
                  </p>
                )}
                <p className="mt-2 text-slate-600">
                  <strong>Assignment Mode:</strong> {ownerAssignmentMode === 'round_robin' ? 'Round-Robin' : 'Manual'}
                  {ownerAssignmentMode === 'round_robin' && accountExecutives.length > 0 && (
                    <span> (distributed across {accountExecutives.length} Account Executives)</span>
                  )}
                </p>
                {targetStage !== 'cold_data' && (
                  <p className="mt-2 text-blue-600">
                    <strong>Target Stage:</strong> {LEAD_STAGES.find(s => s.value === targetStage)?.label || targetStage}
                  </p>
                )}
              </AlertDescription>
            </Alert>

            {/* Warning for missing owners in manual mode */}
            {ownerAssignmentMode === 'manual' && getRowsNeedingOwner() > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{getRowsNeedingOwner()} rows are missing owner assignment</AlertTitle>
                <AlertDescription>
                  Please assign owners to all rows. Rows without owners will not be imported.
                </AlertDescription>
              </Alert>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep('mapping')}>
                <ArrowLeft className="mr-2 w-4 h-4" /> Back to Mapping
              </Button>
              <Button 
                onClick={handleExecuteImport} 
                disabled={isImporting || getImportableCount() === 0}
                data-testid="execute-import-btn"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import {getImportableCount()} Leads
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Results */}
      {currentStep === 'execute' && importResult && (
        <Card data-testid="results-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-500" />
              Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Result Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-700">{importResult.imported_count}</div>
                  <div className="text-sm text-green-600">Imported</div>
                </CardContent>
              </Card>
              {duplicateBehavior === 'skip' && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-amber-700">{importResult.skipped_duplicates_count}</div>
                    <div className="text-sm text-amber-600">Skipped (Duplicates)</div>
                  </CardContent>
                </Card>
              )}
              {duplicateBehavior === 'flag' && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-amber-700">{importResult.flagged_duplicates_count}</div>
                    <div className="text-sm text-amber-600">Flagged Duplicates</div>
                  </CardContent>
                </Card>
              )}
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-red-700">{importResult.failed_count}</div>
                  <div className="text-sm text-red-600">Failed</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-slate-700">{importResult.total_rows}</div>
                  <div className="text-sm text-slate-500">Total Rows</div>
                </CardContent>
              </Card>
            </div>

            {/* Import Batch ID */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Import Batch ID</AlertTitle>
              <AlertDescription>
                <code className="bg-slate-100 px-2 py-1 rounded text-sm">{importResult.import_batch_id}</code>
                <p className="mt-1 text-sm text-slate-500">
                  Use this ID to identify imported leads.
                </p>
              </AlertDescription>
            </Alert>

            {/* Error Report Download */}
            {importResult.failed_rows?.length > 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>{importResult.failed_rows.length} rows failed to import</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>Download the error report to see which rows failed and why.</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={downloadErrorReport}
                    data-testid="download-error-report"
                  >
                    <Download className="mr-2 w-4 h-4" /> Download Error Report
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button onClick={() => navigate('/leads')} data-testid="go-to-leads">
                View Leads
              </Button>
              <Button variant="outline" onClick={resetImport}>
                Import More Leads
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
}

// Source Leads View Component (for Landing Pages and Field Data)
function SourceLeadsView({
  dataSource, sourceLeads, sourceLeadsLoading, assignmentMode, setAssignmentMode,
  manualOwners, onManualOwnerChange, allUsers, accountExecutives, onAssign, onDownload,
  isAssigning, assignResult, onRefresh
}) {
  const sourceLabel = dataSource === 'landing_page' ? 'Landing Page' : 'Field Data';
  const { role } = usePermissions();
  const canBulkDelete = role === 'owner' || role === 'admin';

  // Local UI state for new features
  const [landingPageFilter, setLandingPageFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAssignOwner, setBulkAssignOwner] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [detailLead, setDetailLead] = useState(null);

  // Clearing selections is handled inline in the filter setter and on data source change.
  const handleFilterChange = (value) => {
    setLandingPageFilter(value);
    setSelectedIds(new Set());
  };

  // Derive landing page options from the current leads (for landing_page source)
  const landingPageOptions = (() => {
    if (dataSource !== 'landing_page') return [];
    const map = new Map();
    sourceLeads.forEach((l) => {
      const id = l.landing_page_id || l.landing_page_slug || '';
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, { id, label: l.landing_page_slug || id });
      }
    });
    return Array.from(map.values());
  })();

  // Filter leads by landing page if set
  const filteredLeads = sourceLeads.filter((l) => {
    if (dataSource !== 'landing_page' || landingPageFilter === 'all') return true;
    return (l.landing_page_id || l.landing_page_slug || '') === landingPageFilter;
  });

  const toggleSelect = (leadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const allFilteredIds = filteredLeads.map((l) => l.lead_id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        allFilteredIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      allFilteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  // Clear selections when filter / data source changes
  // (clearing happens inline in handleFilterChange and on data source switching)

  const handleBulkAssign = async () => {
    if (selectedIds.size === 0 || !bulkAssignOwner) {
      toast.error('Select leads and an owner first');
      return;
    }
    setBulkBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/leads/bulk-assign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_ids: Array.from(selectedIds),
          assigned_to: bulkAssignOwner,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Bulk assign failed');
      } else {
        const d = await r.json();
        toast.success(`${d.assigned_count} lead${d.assigned_count === 1 ? '' : 's'} assigned`);
        setSelectedIds(new Set());
        setBulkAssignOwner('');
        onRefresh();
      }
    } catch (err) {
      logger.error('Bulk assign error', err);
      toast.error('Bulk assign failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = window.confirm(
      `Permanently delete ${selectedIds.size} lead${selectedIds.size === 1 ? '' : 's'}? ` +
      `This cannot be undone.`,
    );
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/leads/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_ids: Array.from(selectedIds) }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Bulk delete failed');
      } else {
        const d = await r.json();
        toast.success(`${d.deleted_count} lead${d.deleted_count === 1 ? '' : 's'} deleted`);
        setSelectedIds(new Set());
        onRefresh();
      }
    } catch (err) {
      logger.error('Bulk delete error', err);
      toast.error('Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one lead');
      return;
    }
    try {
      const ids = Array.from(selectedIds).join(',');
      const r = await fetch(
        `${API_URL}/api/leads/import/download-source-leads?source=${dataSource}&lead_ids=${encodeURIComponent(ids)}`,
        { credentials: 'include' },
      );
      if (!r.ok) {
        toast.error('Download failed');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataSource}_selected_leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      logger.error('Selected download error', err);
      toast.error('Download failed');
    }
  };

  const handleDownloadFiltered = async () => {
    try {
      const params = new URLSearchParams({ source: dataSource });
      if (dataSource === 'landing_page' && landingPageFilter !== 'all') {
        params.append('landing_page_id', landingPageFilter);
      }
      const r = await fetch(
        `${API_URL}/api/leads/import/download-source-leads?${params.toString()}`,
        { credentials: 'include' },
      );
      if (!r.ok) {
        toast.error('Download failed');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataSource}_leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      logger.error('Filtered download error', err);
      toast.error('Download failed');
    }
  };

  if (assignResult) {
    return (
      <Card data-testid="source-assign-result">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            Assignment Complete
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700">{assignResult.assigned_count}</p>
              <p className="text-sm text-green-600">Assigned</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-slate-700">{assignResult.total_requested}</p>
              <p className="text-sm text-slate-600">Total Requested</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">
            Assigned leads will appear in the Leads page under <span className="font-medium">Cold Data</span> stage with the source tag <span className="font-medium">{sourceLabel}</span>.
          </p>
          <div className="flex gap-3 pt-2">
            <Button onClick={onRefresh} variant="outline" data-testid="assign-more">
              <RefreshCw className="w-4 h-4 mr-2" /> Assign More
            </Button>
            <Button onClick={() => window.location.href = '/leads'} data-testid="go-to-leads-source">
              View Leads
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="source-leads-view">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              {dataSource === 'landing_page' ? <Globe className="w-5 h-5" /> : <MapPin className="w-5 h-5" />}
              {sourceLabel} Leads
            </CardTitle>
            <CardDescription>
              {sourceLeadsLoading
                ? 'Loading...'
                : filteredLeads.length === sourceLeads.length
                  ? `${sourceLeads.length} lead${sourceLeads.length !== 1 ? 's' : ''}${dataSource === 'landing_page' ? ' (duplicates flagged, all records shown)' : ''}`
                  : `Showing ${filteredLeads.length} of ${sourceLeads.length} leads (filtered)`}
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-source-leads">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadFiltered} data-testid="download-source-leads">
              <Download className="w-4 h-4 mr-1" /> Export All Filtered
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {sourceLeadsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500">Loading leads...</span>
          </div>
        ) : sourceLeads.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="no-source-leads">
            <p className="text-lg font-medium">No unassigned leads</p>
            <p className="text-sm mt-1">All {sourceLabel} leads have been assigned.</p>
          </div>
        ) : (
          <>
            {/* Landing Page filter — only for landing_page source */}
            {dataSource === 'landing_page' && landingPageOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium text-slate-700">Landing Page:</Label>
                <Select value={landingPageFilter} onValueChange={handleFilterChange}>
                  <SelectTrigger className="w-64" data-testid="landing-page-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All landing pages ({sourceLeads.length})</SelectItem>
                    {landingPageOptions.map((opt) => {
                      const count = sourceLeads.filter(
                        (l) => (l.landing_page_id || l.landing_page_slug || '') === opt.id,
                      ).length;
                      return (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.label} ({count})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Bulk action bar — shows when items selected */}
            {selectedIds.size > 0 && (
              <div
                className="flex flex-wrap items-center gap-3 p-3 border border-amber-300 bg-amber-50 rounded-lg"
                data-testid="bulk-action-bar"
              >
                <span className="text-sm font-medium text-amber-900">
                  {selectedIds.size} selected
                </span>
                <button
                  className="text-xs text-amber-700 underline"
                  onClick={() => setSelectedIds(new Set())}
                  data-testid="bulk-clear-selection"
                >
                  Clear
                </button>
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  <Select value={bulkAssignOwner} onValueChange={setBulkAssignOwner}>
                    <SelectTrigger className="w-56 h-9" data-testid="bulk-assign-owner-select">
                      <SelectValue placeholder="Assign to..." />
                    </SelectTrigger>
                    <SelectContent>
                      {accountExecutives.map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.name} (AE)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={handleBulkAssign}
                    disabled={bulkBusy || !bulkAssignOwner}
                    data-testid="bulk-assign-btn"
                  >
                    {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />}
                    Assign
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadSelected}
                    data-testid="bulk-export-selected"
                  >
                    <Download className="w-4 h-4 mr-1" /> Export Selected
                  </Button>
                  {canBulkDelete && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleBulkDelete}
                      disabled={bulkBusy}
                      data-testid="bulk-delete-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Per-row assignment mode toggle */}
            <div className="flex items-center gap-6 p-3 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium text-slate-700">Per-row mode:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={assignmentMode === 'round_robin'}
                  onChange={() => setAssignmentMode('round_robin')}
                  className="text-amber-500"
                  data-testid="source-round-robin"
                />
                <span className="text-sm">Round Robin ({accountExecutives.length} AEs)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={assignmentMode === 'manual'}
                  onChange={() => setAssignmentMode('manual')}
                  className="text-amber-500"
                  data-testid="source-manual"
                />
                <span className="text-sm">Manual Assignment</span>
              </label>
            </div>

            {/* Leads Table */}
            <div className="border rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Name</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Phone</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Email</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Landing Page</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Created</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Details</th>
                    {assignmentMode === 'manual' && (
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Assign To</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => {
                    const isAssigned = lead.assigned_to && lead.assigned_to !== '';
                    const isDup = lead.is_duplicate === true;
                    const cfCount = Object.keys(lead.custom_fields || {}).length;
                    const isSelected = selectedIds.has(lead.lead_id);
                    return (
                      <tr
                        key={lead.lead_id}
                        className={`border-t hover:bg-slate-50 ${isSelected ? 'bg-amber-50/40' : ''} ${isAssigned ? 'opacity-70' : ''}`}
                        data-testid={`source-lead-${lead.lead_id}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(lead.lead_id)}
                            aria-label={`Select lead ${lead.lead_id}`}
                            data-testid={`select-lead-${lead.lead_id}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {lead.first_name} {lead.last_name}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{lead.phone || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{lead.email || '-'}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 flex-wrap">
                            {isDup && (
                              <span
                                className="px-1.5 py-0.5 bg-red-50 text-red-700 text-xs rounded font-medium"
                                data-testid={`dup-badge-${lead.lead_id}`}
                              >
                                Duplicate
                              </span>
                            )}
                            {isAssigned && (
                              <span className="px-1.5 py-0.5 bg-green-50 text-green-700 text-xs rounded font-medium">
                                Assigned
                              </span>
                            )}
                            {!isDup && !isAssigned && (
                              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded font-medium">
                                New
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600 text-xs">
                          {lead.landing_page_slug || '-'}
                        </td>
                        <td className="px-3 py-2 text-slate-500 text-xs">
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            className="text-xs text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                            onClick={() => setDetailLead(lead)}
                            data-testid={`view-details-${lead.lead_id}`}
                          >
                            <Info className="w-3 h-3" />
                            View {cfCount > 0 && <span className="text-slate-500">({cfCount})</span>}
                          </button>
                        </td>
                        {assignmentMode === 'manual' && (
                          <td className="px-3 py-2">
                            {isAssigned ? (
                              <span className="text-xs text-green-600">Already assigned</span>
                            ) : (
                              <select
                                value={manualOwners[lead.lead_id] || ''}
                                onChange={(e) => onManualOwnerChange(lead.lead_id, e.target.value)}
                                className="w-full text-xs border rounded px-2 py-1"
                                data-testid={`source-owner-${lead.lead_id}`}
                              >
                                <option value="">Select owner...</option>
                                {allUsers.map((u) => (
                                  <option key={u.user_id} value={u.user_id}>
                                    {u.name} ({u.role})
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {filteredLeads.length === 0 && (
                    <tr>
                      <td colSpan={assignmentMode === 'manual' ? 9 : 8} className="px-3 py-8 text-center text-slate-400 text-sm">
                        No leads match the current filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Assign Button (existing per-row workflow) */}
            <div className="flex items-center justify-between pt-2">
              {(() => {
                const unassignedLeads = filteredLeads.filter((l) => !l.assigned_to || l.assigned_to === '');
                const dupCount = filteredLeads.filter((l) => l.is_duplicate === true).length;
                return (
                  <>
                    <div className="text-sm text-slate-500 space-y-1">
                      <p>
                        {assignmentMode === 'round_robin'
                          ? `${unassignedLeads.length} unassigned leads will be distributed across ${accountExecutives.length} Account Executives`
                          : `${Object.values(manualOwners).filter(Boolean).length} of ${unassignedLeads.length} unassigned leads assigned`}
                      </p>
                      {dupCount > 0 && (
                        <p className="text-xs text-amber-600">
                          {dupCount} duplicate{dupCount !== 1 ? 's' : ''} detected — will be skipped during assignment
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={onAssign}
                      disabled={
                        isAssigning ||
                        unassignedLeads.length === 0 ||
                        (assignmentMode === 'manual' && Object.values(manualOwners).filter(Boolean).length === 0)
                      }
                      data-testid="assign-source-leads"
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {isAssigning ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning...
                        </>
                      ) : (
                        <>
                          <Users className="w-4 h-4 mr-2" /> Assign Leads
                        </>
                      )}
                    </Button>
                  </>
                );
              })()}
            </div>
          </>
        )}
      </CardContent>

      {/* Detail modal */}
      {detailLead && (
        <LeadDetailModal lead={detailLead} onClose={() => setDetailLead(null)} />
      )}
    </Card>
  );
}

// Modal showing all submitted fields for a landing-page lead.
function LeadDetailModal({ lead, onClose }) {
  const customFields = lead.custom_fields || {};
  const customEntries = Object.entries(customFields);
  const utmEntries = [
    ['UTM Source', lead.utm_source],
    ['UTM Medium', lead.utm_medium],
    ['UTM Campaign', lead.utm_campaign],
    ['UTM Content', lead.utm_content],
    ['Referrer', lead.referrer],
    ['IP Address', lead.ip_address],
  ].filter(([, v]) => v);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="lead-detail-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-slate-900">
            {lead.first_name} {lead.last_name} — Submission Details
          </h2>
          <button
            className="text-slate-400 hover:text-slate-600"
            onClick={onClose}
            data-testid="close-detail-modal"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          {/* Core fields */}
          <section>
            <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
              Core
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <DetailRow label="Phone" value={lead.phone} />
              <DetailRow label="Email" value={lead.email} />
              <DetailRow label="Stage" value={lead.stage} />
              <DetailRow label="Source" value={lead.lead_source} />
              <DetailRow label="Landing Page" value={lead.landing_page_slug} />
              <DetailRow
                label="Created"
                value={lead.created_at ? new Date(lead.created_at).toLocaleString() : '-'}
              />
              <DetailRow
                label="Duplicate"
                value={lead.is_duplicate ? `Yes (of ${lead.duplicate_of || 'existing'})` : 'No'}
              />
              <DetailRow label="Product Interest" value={lead.product_interest} />
            </dl>
          </section>

          {/* Custom fields */}
          <section>
            <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
              Form Fields ({customEntries.length})
            </h3>
            {customEntries.length === 0 ? (
              <p className="text-slate-400 text-sm">No custom fields submitted.</p>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="custom-fields-list">
                {customEntries.map(([k, v]) => (
                  <DetailRow key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
                ))}
              </dl>
            )}
          </section>

          {/* Notes */}
          {lead.notes && (
            <section>
              <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                Notes
              </h3>
              <pre className="text-xs bg-slate-50 p-3 rounded border whitespace-pre-wrap break-words">
                {lead.notes}
              </pre>
            </section>
          )}

          {/* UTM */}
          {utmEntries.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                Attribution
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {utmEntries.map(([label, v]) => (
                  <DetailRow key={label} label={label} value={v} />
                ))}
              </dl>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm text-slate-800 break-words">{value || <span className="text-slate-400">—</span>}</span>
    </div>
  );
}

